import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { Shield, User, Clock, FileText } from 'lucide-react';

interface RoleAuditEntry {
  id: string;
  user_id: string;
  changed_by: string;
  old_role: string | null;
  new_role: string;
  reason: string | null;
  changed_at: string;
  user_name: string;
  changed_by_name: string;
}

export const RoleAuditLog: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<RoleAuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchAuditLogs();
    
    // Subscribe to real-time updates for new audit log entries
    const subscription = supabase
      .channel('role_audit_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'role_change_audit'
        },
        (payload) => {
          // Refetch logs when new entry is added
          fetchAuditLogs();
        }
      )
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchAuditLogs = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('role_change_audit')
        .select(`
          *,
          user:profiles!role_change_audit_user_id_fkey(first_name, last_name, email),
          changed_by_user:profiles!role_change_audit_changed_by_fkey(first_name, last_name, email)
        `)
        .order('changed_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const formattedLogs = data?.map((log: any) => ({
        id: log.id,
        user_id: log.user_id,
        changed_by: log.changed_by,
        old_role: log.old_role,
        new_role: log.new_role,
        reason: log.reason,
        changed_at: log.changed_at,
        user_name: `${log.user?.first_name || ''} ${log.user?.last_name || ''}`.trim() || 'Unknown User',
        changed_by_name: `${log.changed_by_user?.first_name || ''} ${log.changed_by_user?.last_name || ''}`.trim() || 'System'
      })) || [];

      setAuditLogs(formattedLogs);
    } catch (error: any) {
      toast({
        title: "Error loading audit logs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'default';
      case 'manager': return 'secondary';
      case 'sales': return 'outline';
      default: return 'secondary';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <CardTitle>Role Change Audit Log</CardTitle>
        </div>
        <CardDescription>
          Track all role changes across the system
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No role changes recorded yet
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-4">
              {auditLogs.map((log) => (
                <div
                  key={log.id}
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{log.user_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatDistanceToNow(new Date(log.changed_at), { addSuffix: true })}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    {log.old_role && (
                      <>
                        <Badge variant={getRoleBadgeVariant(log.old_role)}>
                          {log.old_role}
                        </Badge>
                        <span className="text-muted-foreground">→</span>
                      </>
                    )}
                    <Badge variant={getRoleBadgeVariant(log.new_role)}>
                      {log.new_role}
                    </Badge>
                  </div>

                  <div className="text-sm text-muted-foreground">
                    Changed by: <span className="font-medium text-foreground">{log.changed_by_name}</span>
                  </div>

                  {log.reason && (
                    <div className="mt-3 flex items-start gap-2 text-sm">
                      <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div className="text-muted-foreground">
                        <span className="font-medium text-foreground">Reason:</span> {log.reason}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
